import React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { getRoute, BASE_URL } from '@/lib/routes'
import { LOCALES, getLocaleConfig, isUnlocalizedPath } from '@/i18n/config'
import { localizedPath } from '@/lib/localizedPath'

interface SEOProps {
  title?: string;
  description?: string;
  type?: string;
  path: string;
  image?: string;
  noindex?: boolean;
}

const DEFAULT_OG_IMAGE = '/og-banner.png';

const SEO: React.FC<SEOProps> = ({ path, title, description, type, image, noindex }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const route = getRoute(path);

  const meta = {
    title: title || (route ? t(`seo.${route.seoKey}.title`) : undefined) || 'Currents',
    description: description || (route ? t(`seo.${route.seoKey}.description`) : undefined),
    type: type || 'website'
  };

  const canonicalUrl = `${BASE_URL}${localizedPath(path, lang)}`;
  const imagePath = image || DEFAULT_OG_IMAGE;
  const imageUrl = imagePath.startsWith('http') ? imagePath : `${BASE_URL}${imagePath}`;
  // The blog has no per-locale URL, so it gets no hreflang alternates -
  // there's nothing for search engines to point them at.
  const hasLocaleVariants = !isUnlocalizedPath(path);

  return (
    <Helmet htmlAttributes={{ lang: getLocaleConfig(lang).htmlLang }}>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* OpenGraph / Facebook */}
      <meta property="og:type" content={meta.type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={imageUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Language alternates */}
      {hasLocaleVariants && LOCALES.map((locale) => (
        <link
          key={locale.code}
          rel="alternate"
          hrefLang={locale.htmlLang}
          href={`${BASE_URL}${localizedPath(path, locale.code)}`}
        />
      ))}
      {hasLocaleVariants && <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${path}`} />}
    </Helmet>
  );
};

export default SEO; 